import { ApiService } from "./api.service";
import type { TResolvedTemplateCustomField } from "@/types/project-template";

export interface WorkItemCustomFieldValueResponse {
  custom_fields_schema: TResolvedTemplateCustomField[];
  custom_field_values: Record<string, unknown>;
}

/**
 * Service for the per-work-item global custom-fields overlay (PRD §7).
 *
 * Mirrors :class:`ProjectCustomFieldsService` but for work items. Endpoints are
 * admin-gated server-side (``is_project_admin`` on the work item's owning
 * project). Each call returns the work item's resolved schema after the
 * mutation so the caller can refresh local state without a follow-up GET.
 */
export class WorkItemCustomFieldsService extends ApiService {
  async addFields(
    slug: string,
    projectId: string,
    workItemId: string,
    fieldIds: string[],
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.post(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/custom-fields/`,
      { field_ids: fieldIds },
    );
  }

  async updateRequired(
    slug: string,
    projectId: string,
    workItemId: string,
    fieldId: string,
    required: boolean,
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/custom-fields/${fieldId}/`,
      { required },
    );
  }

  async removeField(
    slug: string,
    projectId: string,
    workItemId: string,
    fieldId: string,
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/custom-fields/${fieldId}/`,
    );
  }

  async reorderFields(
    slug: string,
    projectId: string,
    workItemId: string,
    orderedFieldIds: string[],
  ): Promise<TResolvedTemplateCustomField[]> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/custom-fields/`,
      { order: orderedFieldIds },
    );
  }

  /**
   * Write a single field's value on a work item (admin-only). ``null`` clears
   * the value. The BE returns the resolved schema *and* the updated
   * ``custom_field_values`` dict so callers can refresh state in one shot.
   */
  async setValue(
    slug: string,
    projectId: string,
    workItemId: string,
    fieldId: string,
    value: unknown,
  ): Promise<WorkItemCustomFieldValueResponse> {
    return this.patch(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/custom-fields/${fieldId}/value/`,
      { value },
    );
  }
}
